import type { ICountries } from '../sharedTypes';

export interface IParsedGooglePlaceAddress {
  city: string;
  description: string;
  matchedCountry: ICountries | null;
  postcode: string;
  state: string;
  street: string;
}

const scoreAddressComponents = (components: google.maps.GeocoderAddressComponent[]) =>
  ['street_number', 'route', 'locality', 'postal_code', 'administrative_area_level_1'].reduce(
    (score, type) =>
      components.some((component) => component.types.includes(type)) ? score + 1 : score,
    0
  );

const geocodeByPlaceId = async (placeId: string) =>
  new Promise<google.maps.GeocoderResult[] | null>((resolve) => {
    new google.maps.Geocoder().geocode({ placeId }, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK || !results?.[0]) {
        resolve(null);
        return;
      }

      resolve(results);
    });
  });

export const parseGooglePlacePrediction = async (
  prediction: google.maps.places.AutocompletePrediction,
  countries: ICountries[] = []
): Promise<IParsedGooglePlaceAddress | null> => {
  const results = await geocodeByPlaceId(prediction.place_id);

  if (!results) return null;

  const bestResult = [...results].sort(
    (a, b) =>
      scoreAddressComponents(b.address_components) - scoreAddressComponents(a.address_components)
  )[0];
  const bestComponents = bestResult?.address_components ?? [];
  const allComponents = results.flatMap((result) => result.address_components);
  const getComponentName = (
    component: google.maps.GeocoderAddressComponent | undefined,
    nameType: 'long_name' | 'short_name'
  ) => {
    if (!component) return '';

    return nameType === 'short_name' ? component.short_name : component.long_name;
  };
  const get = (type: string, nameType: 'long_name' | 'short_name' = 'long_name') =>
    getComponentName(
      bestComponents.find((component) => component.types.includes(type)),
      nameType
    ) ||
    getComponentName(
      allComponents.find((component) => component.types.includes(type)),
      nameType
    );

  const streetNumber = get('street_number');
  const route = get('route');
  const street = [streetNumber, route].filter(Boolean).join(' ').trim();
  const city = get('locality') || get('sublocality') || get('administrative_area_level_2');
  const state = get('administrative_area_level_1');
  const postcode = get('postal_code');
  const countryName = get('country');
  const matchedCountry =
    countries.find((country) => {
      const countryValues = [country.name, country.printable_name].filter(
        (value): value is string => Boolean(value)
      );

      return countryValues.some((value) => value.toUpperCase() === countryName.toUpperCase());
    }) ?? null;

  return {
    city,
    description: prediction.description ?? '',
    matchedCountry,
    postcode,
    state,
    street
  };
};
